import { FaReceipt, FaFileInvoiceDollar, FaClipboardList, FaChartLine, FaBuilding, FaIndustry, FaLeaf, FaPalette, FaGem, FaRocket } from 'react-icons/fa';

export interface InvoiceTemplate {
  id: string;
  name: {
    ar: string;
    en: string;
  };
  description: {
    ar: string;
    en: string;
  };
  icon: React.ReactNode;
  color: string;
  price: string;
  isPremium: boolean;
  atsCompatibility: {
    score: number;
    features: string[];
    recommendations: string[];
  };
  layout: {
    headerStyle: 'modern' | 'classic' | 'minimal' | 'corporate';
    tableStyle: 'clean' | 'bordered' | 'striped' | 'elegant';
    footerStyle: 'simple' | 'detailed' | 'professional';
    colorScheme: 'blue' | 'green' | 'purple' | 'orange' | 'gray';
  };
}

export const invoiceTemplates: InvoiceTemplate[] = [
  {
    id: 'modern-clean',
    name: {
      ar: 'حديث ونظيف',
      en: 'Modern Clean'
    },
    description: {
      ar: 'تصميم حديث ونظيف مع تخطيط بسيط وألوان هادئة. مثالي للشركات الناشئة والمشاريع الصغيرة.',
      en: 'Modern and clean design with simple layout and calm colors. Perfect for startups and small businesses.'
    },
    icon: <FaReceipt />,
    color: 'from-blue-500 to-blue-600',
    price: 'مجاني',
    isPremium: false,
    atsCompatibility: {
      score: 95,
      features: [
        'تخطيط واضح ومنظم',
        'ألوان احترافية',
        'خطوط مقروءة',
        'مساحات متوازنة'
      ],
      recommendations: [
        'مثالي للفواتير البسيطة',
        'سهل القراءة والطباعة',
        'متوافق مع جميع الأجهزة'
      ]
    },
    layout: {
      headerStyle: 'modern',
      tableStyle: 'clean',
      footerStyle: 'simple',
      colorScheme: 'blue'
    }
  },
  {
    id: 'corporate-professional',
    name: {
      ar: 'مهني للشركات',
      en: 'Corporate Professional'
    },
    description: {
      ar: 'تصميم احترافي للشركات الكبيرة مع تفاصيل دقيقة وألوان رسمية. مناسب للشركات المتوسطة والكبيرة.',
      en: 'Professional design for large companies with detailed information and formal colors. Suitable for medium and large companies.'
    },
    icon: <FaBuilding />,
    color: 'from-gray-600 to-gray-700',
    price: 'مجاني',
    isPremium: false,
    atsCompatibility: {
      score: 98,
      features: [
        'تصميم احترافي عالي الجودة',
        'تفاصيل شاملة',
        'ألوان رسمية',
        'تخطيط منظم للغاية'
      ],
      recommendations: [
        'مثالي للشركات الكبيرة',
        'يحتوي على جميع التفاصيل المطلوبة',
        'مظهر احترافي ومقنع'
      ]
    },
    layout: {
      headerStyle: 'corporate',
      tableStyle: 'bordered',
      footerStyle: 'detailed',
      colorScheme: 'gray'
    }
  },
  {
    id: 'elegant-premium',
    name: {
      ar: 'أنيق مميز',
      en: 'Elegant Premium'
    },
    description: {
      ar: 'تصميم أنيق ومميز مع تفاصيل فريدة وألوان جذابة. مثالي للشركات الفاخرة والمشاريع الخاصة.',
      en: 'Elegant and premium design with unique details and attractive colors. Perfect for luxury companies and special projects.'
    },
    icon: <FaGem />,
    color: 'from-purple-500 to-purple-600',
    price: '50 ريال',
    isPremium: true,
    atsCompatibility: {
      score: 92,
      features: [
        'تصميم فريد وأنيق',
        'ألوان جذابة ومميزة',
        'تفاصيل فنية دقيقة',
        'مظهر فاخر'
      ],
      recommendations: [
        'مثالي للمشاريع الفاخرة',
        'يترك انطباعاً قوياً',
        'مميز عن التصاميم العادية'
      ]
    },
    layout: {
      headerStyle: 'modern',
      tableStyle: 'elegant',
      footerStyle: 'professional',
      colorScheme: 'purple'
    }
  },
  {
    id: 'minimal-simple',
    name: {
      ar: 'بسيط وواضح',
      en: 'Minimal Simple'
    },
    description: {
      ar: 'تصميم بسيط وواضح مع أقل قدر من التفاصيل. مثالي للفواتير السريعة والمشاريع البسيطة.',
      en: 'Simple and clear design with minimal details. Perfect for quick invoices and simple projects.'
    },
    icon: <FaClipboardList />,
    color: 'from-gray-400 to-gray-500',
    price: 'مجاني',
    isPremium: false,
    atsCompatibility: {
      score: 90,
      features: [
        'تصميم بسيط وواضح',
        'سهل القراءة',
        'طباعة سريعة',
        'ألوان هادئة'
      ],
      recommendations: [
        'مثالي للفواتير السريعة',
        'سهل الفهم والقراءة',
        'مناسب للمشاريع البسيطة'
      ]
    },
    layout: {
      headerStyle: 'minimal',
      tableStyle: 'clean',
      footerStyle: 'simple',
      colorScheme: 'gray'
    }
  },
  {
    id: 'business-focused',
    name: {
      ar: 'مركز على الأعمال',
      en: 'Business Focused'
    },
    description: {
      ar: 'تصميم يركز على الجوانب التجارية مع تفاصيل مالية واضحة. مثالي للشركات التجارية والاستشارات.',
      en: 'Design focused on business aspects with clear financial details. Perfect for commercial companies and consultancies.'
    },
    icon: <FaChartLine />,
    color: 'from-blue-600 to-blue-700',
    price: 'مجاني',
    isPremium: false,
    atsCompatibility: {
      score: 96,
      features: [
        'تركيز على الجوانب المالية',
        'تفاصيل تجارية واضحة',
        'ألوان احترافية',
        'تخطيط منظم'
      ],
      recommendations: [
        'مثالي للشركات التجارية',
        'يركز على التفاصيل المالية',
        'مظهر احترافي'
      ]
    },
    layout: {
      headerStyle: 'corporate',
      tableStyle: 'striped',
      footerStyle: 'detailed',
      colorScheme: 'blue'
    }
  },
  {
    id: 'industrial-robust',
    name: {
      ar: 'صناعي قوي',
      en: 'Industrial Robust'
    },
    description: {
      ar: 'تصميم قوي ومتين للشركات الصناعية مع تفاصيل تقنية. مثالي للشركات الصناعية والتصنيع.',
      en: 'Strong and robust design for industrial companies with technical details. Perfect for industrial and manufacturing companies.'
    },
    icon: <FaIndustry />,
    color: 'from-orange-500 to-orange-600',
    price: '30 ريال',
    isPremium: true,
    atsCompatibility: {
      score: 94,
      features: [
        'تصميم قوي ومتين',
        'تفاصيل تقنية دقيقة',
        'ألوان صناعية',
        'مظهر احترافي'
      ],
      recommendations: [
        'مثالي للشركات الصناعية',
        'يحتوي على تفاصيل تقنية',
        'مظهر قوي ومقنع'
      ]
    },
    layout: {
      headerStyle: 'corporate',
      tableStyle: 'bordered',
      footerStyle: 'professional',
      colorScheme: 'orange'
    }
  },
  {
    id: 'eco-friendly',
    name: {
      ar: 'صديق للبيئة',
      en: 'Eco Friendly'
    },
    description: {
      ar: 'تصميم أخضر وصديق للبيئة مع ألوان طبيعية. مثالي للشركات البيئية والمشاريع الخضراء.',
      en: 'Green and eco-friendly design with natural colors. Perfect for environmental companies and green projects.'
    },
    icon: <FaLeaf />,
    color: 'from-green-500 to-green-600',
    price: '25 ريال',
    isPremium: true,
    atsCompatibility: {
      score: 93,
      features: [
        'تصميم صديق للبيئة',
        'ألوان طبيعية هادئة',
        'مظهر عضوي',
        'تخطيط مستدام'
      ],
      recommendations: [
        'مثالي للشركات البيئية',
        'يعكس الاهتمام بالبيئة',
        'مظهر طبيعي ومريح'
      ]
    },
    layout: {
      headerStyle: 'modern',
      tableStyle: 'clean',
      footerStyle: 'detailed',
      colorScheme: 'green'
    }
  },
  {
    id: 'creative-artistic',
    name: {
      ar: 'إبداعي فني',
      en: 'Creative Artistic'
    },
    description: {
      ar: 'تصميم إبداعي وفني مع ألوان متعددة وتفاصيل مميزة. مثالي للشركات الإبداعية والفنون.',
      en: 'Creative and artistic design with multiple colors and unique details. Perfect for creative companies and arts.'
    },
    icon: <FaPalette />,
    color: 'from-pink-500 to-purple-500',
    price: '40 ريال',
    isPremium: true,
    atsCompatibility: {
      score: 89,
      features: [
        'تصميم إبداعي وفني',
        'ألوان متعددة وجذابة',
        'تفاصيل فريدة',
        'مظهر مميز'
      ],
      recommendations: [
        'مثالي للشركات الإبداعية',
        'يترك انطباعاً فنياً',
        'مميز عن التصاميم التقليدية'
      ]
    },
    layout: {
      headerStyle: 'modern',
      tableStyle: 'elegant',
      footerStyle: 'professional',
      colorScheme: 'purple'
    }
  },
  {
    id: 'tech-startup',
    name: {
      ar: 'تقني للشركات الناشئة',
      en: 'Tech Startup'
    },
    description: {
      ar: 'تصميم تقني حديث للشركات الناشئة مع ألوان ديناميكية. مثالي للشركات التقنية والاستثمارات.',
      en: 'Modern tech design for startups with dynamic colors. Perfect for tech companies and investments.'
    },
    icon: <FaRocket />,
    color: 'from-indigo-500 to-purple-600',
    price: '35 ريال',
    isPremium: true,
    atsCompatibility: {
      score: 91,
      features: [
        'تصميم تقني حديث',
        'ألوان ديناميكية',
        'مظهر عصري',
        'تخطيط مبتكر'
      ],
      recommendations: [
        'مثالي للشركات التقنية',
        'يعكس الابتكار والتطور',
        'مظهر عصري ومتقدم'
      ]
    },
    layout: {
      headerStyle: 'corporate',
      tableStyle: 'striped',
      footerStyle: 'detailed',
      colorScheme: 'purple'
    }
  },
  {
    id: 'classic-traditional',
    name: {
      ar: 'تقليدي كلاسيكي',
      en: 'Classic Traditional'
    },
    description: {
      ar: 'تصميم تقليدي كلاسيكي مع ألوان محافظة وتخطيط رسمي. مثالي للشركات التقليدية والمؤسسات.',
      en: 'Traditional classic design with conservative colors and formal layout. Perfect for traditional companies and institutions.'
    },
    icon: <FaFileInvoiceDollar />,
    color: 'from-gray-700 to-gray-800',
    price: 'مجاني',
    isPremium: false,
    atsCompatibility: {
      score: 97,
      features: [
        'تصميم تقليدي محترم',
        'ألوان محافظة',
        'تخطيط رسمي',
        'مظهر موثوق'
      ],
      recommendations: [
        'مثالي للشركات التقليدية',
        'يعكس الاحترام والثقة',
        'مظهر رسمي ومحترم'
      ]
    },
    layout: {
      headerStyle: 'classic',
      tableStyle: 'bordered',
      footerStyle: 'simple',
      colorScheme: 'gray'
    }
  }
];

export default invoiceTemplates; 